/* eslint-disable function-paren-newline */
/* eslint-disable react/jsx-curly-newline */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Image from 'next/image';
import Spinner from '@components/common/SmartCalendar/Spinner';
import AddedToCartPopup from '@components/common/AddedToCartPopup/AddedToCartPopup';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { CartItemDetails } from '@customTypes/cart-items';
import useSessionStorage from '@hooks/useSessionStorage';
import { store } from '@context/store';
import {
  getDateWithDashes,
  getDateWithSlashes,
  isWeekend,
  pastEscapedSearchDate,
} from '@helpers/date';
import HotelDetails from '@customTypes/hotel-details';
import { ShouldShowFairPricingFromHotelDetails } from '@utils/fairPricing';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import AvailabilityWidgetDLP from '@components/AvailabilityWidget/AvailabilityWidgetDLP';
import FixedBottomBanner from '@components/common/FixedBottomBanner/FixedBottomBanner';
import BottomDrawer from '@components/common/BottomDrawer/BottomDrawer';
import { CheckoutInfo } from '@customTypes/checkout-info';
import { isAnyProductAvailable } from '@helpers/product';
import YourCart from '@components/YourCart/YourCart';
import { dlpSmartCalendarOpen, productCategoryClicked } from '@events/DLP';
import MobileProductDetails from '@components/ProductDetails/MobileProductDetails';
import SameDayBookingWarning from '@components/Popups/SameDayBookingWarning/SameDayBookingWarning';
import { useEvents } from '@events/EventsProvider';
import { SELECT_SEARCH_ON_VIEW_DLP } from '@constants/amplitudeEvents';
import RoutingPath from '@constants/routingPath';
import { deleteCartAndRemoveKeys } from '@helpers/cartFunctions';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import SmartCalendarMobile from '@components/common/SmartCalendar/MobileSmartCalendar/SmartCalendarMobile';
import Currency from '@components/common/Currrency/Currency';
import actionTypes from '@context/actionTypes';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import YourCartWithSTP from '@components/STP/YourCart/YourCartWithSTP';
import { filterProductsByCategory, getUniqueProductCategories } from '@helpers/filters';
import { useAmplitudeApplyProductCategoryTracker } from '@hooks/useAmplitudeTrackers';
import { getCartItems, getCheckoutInfo, getProductDetails } from '../../utils/services';
import calendarIcon from '../../assets/images/calendar-icon-new.svg';
import { ProductDetails as ProductDetailsData } from '../../types/product-details';

type Props = {
  productDetails: ProductDetailsData | undefined;
  setProductDetails: React.Dispatch<React.SetStateAction<ProductDetailsData | undefined>>;
  hotelId: number | undefined;
  hotelDetails: HotelDetails;
  urlDate: string | undefined;
  scrollToBookingSection: () => void;
  productCategory: string | null;
  handleCategoryChange: (category: string) => void;
};

type BottomDrawerState = {
  [key: string]: boolean;
};

type SetBottomDrawerState = Dispatch<SetStateAction<BottomDrawerState>>;

export default function MobileBookingSection({
  productDetails,
  hotelId,
  hotelDetails,
  urlDate,
  setProductDetails,
  scrollToBookingSection,
  productCategory,
  handleCategoryChange,
}: Props) {
  const [productData, setProductData] = useState<ProductDetailsData | undefined>(productDetails);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState<boolean>(false);
  const [isAddedToCartPopupOpen, setIsAddedToCartPopupOpen] = useState<boolean>(false);
  const [isYourCartExpanded, setIsYourCartExpanded] = useState<boolean>(false);
  const [cartData, setCartData] = useState<CartItemDetails>();
  const [viewProductEventFired, setViewProductEventFired] = useState<boolean>(false);
  const [checkoutDetails, setCheckoutDetails] = useState<CheckoutInfo>();
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false);
  const [bottomDrawerState, setBottomDrawerState]: [BottomDrawerState, SetBottomDrawerState] =
    useState({});
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>(
    productCategory || 'All',
  );

  const router = useRouter();
  const { track } = useEvents();
  const { getItem, setItem } = useSessionStorage();
  const cartId = getItem(sessionStorageKeys.CART_ID);
  const smartCalendarDate = getItem(sessionStorageKeys.SMART_CALENDAR_DATE);

  const { dispatch, state } = useContext(store);
  const { pricingMarkdownExperiment } = state;
  const { searchDate } = state;
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;

  const showFairPricing = ShouldShowFairPricingFromHotelDetails(hotelDetails);
  const amplitudeApplyProductCategoryTracker = useAmplitudeApplyProductCategoryTracker('dlp');

  const setConfirmedDate = useCallback(
    (date: Date) => {
      dispatch({
        type: actionTypes.SET_SEARCH_DATE,
        payload: date,
      });
    },
    [dispatch],
  );

  const renderAvailabilityWidget = () => {
    if (
      smartCalendarDate &&
      isWeekend(new Date(smartCalendarDate)) &&
      productDetails &&
      !isAnyProductAvailable(productDetails.products)
    ) {
      return (
        <div>
          <AvailabilityWidgetDLP cityID={hotelDetails.city_id} />
        </div>
      );
    }

    return null;
  };

  const getProductsByCategory = useCallback(
    (category: string) => filterProductsByCategory(productData?.products, category),
    [productData],
  );

  const categories = useMemo(
    () => getUniqueProductCategories(productData?.products),
    [productData],
  );

  const filteredProducts = useMemo(() => {
    const category = selectedProductCategory ?? 'All';
    const p = getProductsByCategory(category);
    if (p.length) return p;
    if (category !== 'All') {
      setSelectedProductCategory('All');
      return getProductsByCategory('All');
    }
    return [];
  }, [getProductsByCategory, setSelectedProductCategory, selectedProductCategory]);

  const onCategoryClick = (category: string) => {
    setSelectedProductCategory(category);
    handleCategoryChange(category);
    amplitudeApplyProductCategoryTracker(category);

    // Event
    productCategoryClicked(category);
  };

  const handleDatePickerModalOpening = () => {
    setIsDatePickerModalOpen(true);

    // Event
    dlpSmartCalendarOpen();
  };

  const onDatePickerConfirmClick = (date: Date) => {
    // Cart Reset
    if (cartId) {
      deleteCartAndRemoveKeys(cartId, dispatch, user);
      setCartData(undefined);
    }

    // Confirm the date
    setConfirmedDate(date);
    setItem(
      sessionStorageKeys.SMART_CALENDAR_DATE,
      date ? getDateWithSlashes(pastEscapedSearchDate(date, dispatch)) : '',
    );

    // Event
    if (router.pathname.includes(RoutingPath.DLP) && date) {
      track(SELECT_SEARCH_ON_VIEW_DLP, {
        hotel_id: hotelId,
        search_date: getDateWithDashes(date),
      });
    }
  };

  // Sync state when user navigates with back/forward buttons
  useEffect(() => {
    if (router.query.product_category) {
      setSelectedProductCategory(router.query.product_category as string);
    } else {
      setSelectedProductCategory('All');
    }
  }, [router.query.product_category]);

  useEffect(() => {
    (async () => {
      try {
        if (hotelId && searchDate) {
          setIsSpinnerLoading(true);
          const productDetailsData = await getProductDetails(
            hotelId,
            getDateWithSlashes(searchDate),
          );
          setProductData(productDetailsData);
          setProductDetails(productDetailsData);
          setIsSpinnerLoading(false);
        }
      } catch (error) {
        setIsSpinnerLoading(false);
        // TODO: Display error message here
      }
    })();
  }, [cartId, searchDate, hotelId, setProductDetails]);

  useEffect(() => {
    setProductData(productDetails);
  }, [productDetails]);

  useEffect(() => {
    if (urlDate && !searchDate) {
      const [y, m, d] = urlDate.split('-');
      if (y && m && d) {
        setConfirmedDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)));
      } else {
        setConfirmedDate(new Date(urlDate));
      }
    }
  }, [searchDate, setConfirmedDate, urlDate]);

  useEffect(() => {
    if (productDetails && !viewProductEventFired) {
      setViewProductEventFired(true);
    }
  }, [hotelDetails, productDetails, viewProductEventFired]);

  useEffect(() => {
    if (smartCalendarDate) {
      setConfirmedDate(new Date(smartCalendarDate));
    }
  }, [setConfirmedDate, smartCalendarDate]);

  const waitFor = async (milliseconds: number) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(1);
      }, milliseconds);
    });

  useEffect(() => {
    if (cartId) {
      waitFor(500).then(async () => {
        const cartItems = await getCartItems(cartId, user);
        setCartData(cartItems);
      });
    }
  }, [cartId, user]);

  useEffect(() => {
    (async () => {
      try {
        if (
          cartData &&
          cartData?.items.length > 0 &&
          cartData?.items[0] &&
          cartData?.items[0].hotel_id
        ) {
          const checkoutData = await getCheckoutInfo(cartId, user);
          if (checkoutData && checkoutData.extracted_items.length > 0) {
            setCheckoutDetails(checkoutData);
          }
        } else {
          setCheckoutDetails(undefined);
        }
      } catch (error: any) {
        // Ignore error
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData]);

  useEffect(() => {
    if (state.isDatePickerOpen) {
      handleDatePickerModalOpening();
    }
  }, [state.isDatePickerOpen]);

  const isAnyDrawerOpen = useMemo(
    () => Object.values(bottomDrawerState).some((value) => value === true),
    [bottomDrawerState],
  );

  return (
    <>
      <div
        className="px-0 flex flex-col mx-auto w-full mt-5 text-rp-primary-black"
        id="booking-section"
        data-testid="booking-section"
      >
        <div
          className="flex flex-col w-full border-b-8 border-rp-gray-divider pb-26px px-4"
          id="smart-calendar-container"
        >
          <p className="font-rp-gelica-light leading-26px mb-4 text-22 text-rp-primary-black">
            When&apos;s your <span className="font-rp-gelica-light-italic">daycation?</span>
          </p>

          {/* Smart Calendar */}
          <div className="border border-rp-dark-border flex items-center space-x-4 py-5 px-3 rounded-lg">
            <div className="flex-shrink-0 h-4 relative w-4">
              <Image src={calendarIcon} layout="fill" alt="calendar" />
            </div>

            <input
              className="smart-calendar-input text-base font-rp-pn-regular font-semibold leading-4 w-full"
              onClick={handleDatePickerModalOpening}
              placeholder="Select a date"
              readOnly
              type="text"
              value={searchDate ? format(searchDate, 'MMMM dd, yyyy') : ''}
            />
          </div>

          {/* HOT SPOT */}
          {!searchDate && hotelDetails.hot_spot ? (
            <div className="font-medium leading-14px mt-4 text-rp-hot-spot text-sm -tracking-0.28px">
              Save{' '}
              {hotelDetails.hot_spot.discount_type === 'cash' ? (
                <Currency
                  value={hotelDetails.hot_spot.discount}
                  isoCodeClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                  valueClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                />
              ) : (
                `${hotelDetails.hot_spot.discount}%`
              )}{' '}
              when you book between {hotelDetails.hot_spot.start_visit_date} -
              {hotelDetails.hot_spot.end_visit_date}
            </div>
          ) : null}

          <SmartCalendarMobile
            hotel={hotelDetails}
            isModalOpen={isDatePickerModalOpen}
            onConfirmClick={onDatePickerConfirmClick}
            productDetails={productData}
            setIsModalOpen={setIsDatePickerModalOpen}
            previouslySelectedDate={searchDate}
          />
        </div>

        {renderAvailabilityWidget()}

        {/* Product Details */}
        <div
          data-testid="products-section"
          className="relative px-4 mt-10"
          id="product-card-container"
        >
          {isSpinnerLoading && !isAnyDrawerOpen ? <Spinner /> : null}

          <p className="font-rp-gelica-light leading-26px text-22 text-rp-primary-black">
            Products
          </p>

          <div
            data-testid="product-categories-filters"
            className="absolute flex flex-row items-center left-0 mt-4 no-scrollbar overflow-x-auto space-x-2 w-full"
          >
            {categories.map((category, index, { length }) => (
              <div
                key={category}
                className={`${index + 1 === length ? 'pr-4' : ''} ml-4 flex-shrink-0`}
              >
                <button
                  type="button"
                  onClick={() => onCategoryClick(category)}
                  className={`${
                    category.toLowerCase() === selectedProductCategory.toLowerCase()
                      ? 'text-white bg-rp-primary-black border-rp-primary-black'
                      : 'border-rp-gray-divider'
                  } border capitalize font-rp-primary-black font-rp-pn-regular font-semibold leading-17px px-2 py-1 rounded-md flex-shrink-0 text-sm`}
                >
                  {category}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 space-y-4 flex flex-col">
            {filteredProducts &&
              filteredProducts.map((product) => (
                <div
                  data-testid="product"
                  key={product.is_product_time_group ? product.name : product.product_id}
                >
                  <MobileProductDetails
                    showFairPricing={showFairPricing}
                    cartData={cartData}
                    date={searchDate}
                    hotelDetails={hotelDetails}
                    product={product}
                    products={productData?.products}
                    setCartData={setCartData}
                    loadingProducts={isSpinnerLoading}
                    isBottomDrawerOpen={
                      bottomDrawerState[
                        product.product_id
                          ? `product_id-${product.product_id}`
                          : `product_time_group_id-${product.product_time_group_id}`
                      ]
                    }
                    setIsBottomDrawerOpen={() =>
                      setBottomDrawerState((prevState) => ({
                        ...prevState,
                        [product.product_id
                          ? `product_id-${product.product_id}`
                          : `product_time_group_id-${product.product_time_group_id}`]:
                          !prevState[
                            product.product_id
                              ? `product_id-${product.product_id}`
                              : `product_time_group_id-${product.product_time_group_id}`
                          ],
                      }))
                    }
                  />
                </div>
              ))}
          </div>
        </div>

        {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
          <p className="border-rp-gray-divider border-t leading-21px mt-4 mx-4 pt-4 text-center text-sm">
            Pricing and availability are not guaranteed until checkout
          </p>
        ) : null}

        <AddedToCartPopup isOpen={isAddedToCartPopupOpen} setIsOpen={setIsAddedToCartPopupOpen} />

        <BottomDrawer isOpen={isYourCartExpanded} setIsOpen={setIsYourCartExpanded}>
          {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
            <YourCartWithSTP
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
            />
          ) : (
            <YourCart
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
              productData={undefined}
              borderColor=""
              urlDate={undefined}
              productsRef={null}
              showDateWarning={false}
              setShowDateWarning={() => null}
              setBorderColor={() => null}
              setConfirmedDate={() => null}
            />
          )}
        </BottomDrawer>
      </div>

      {state.showSameDayBookingWarning && (
        <SameDayBookingWarning setCartData={setCartData} checkoutDetails={checkoutDetails} />
      )}

      <FixedBottomBanner
        showFairPricing={showFairPricing}
        localDate={searchDate}
        onBookNowClick={scrollToBookingSection}
        cartData={cartData}
        hotelId={hotelId}
        cartId={cartId}
        hotelDetails={hotelDetails}
        setIsYourCartExpanded={setIsYourCartExpanded}
        checkoutDetails={checkoutDetails}
        dispatch={dispatch}
        setCartData={setCartData}
      />
    </>
  );
}
